import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Typography, 
  Card, 
  CardContent,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { 
  CameraAlt, 
  Visibility, 
  VisibilityOff, 
  Save, 
  Notifications, 
  Security, 
  Lock, 
  Email, 
  Phone, 
  Person, 
  Delete, 
  History,
  CloudUpload,
  ArrowBack,
  Verified,
  Settings,
  LocationOn,
  AccountCircle,
  Help,
  Edit,
  Cancel
} from '@mui/icons-material';
import { validate } from 'email-validator';
import SecureLS from 'secure-ls';
import { useNavigate } from 'react-router-dom';
import user_api from '../../api/user';

const ls = new SecureLS({ encodingType: "aes" });

const ProfileSettings = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [editMode, setEditMode] = useState(false);
  
  // State for showing/hiding passwords
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Profile picture
  const [profilePicture, setProfilePicture] = useState(null);
  
  // Notification preferences
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotifications: true,
    pushNotifications: true,
    marketingEmails: false,
    securityAlerts: true,
  });
  
  // Profile data state
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
    // address: '',
    city: '',
    region: '',
    // country: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    isVerified: false,
    lastLogin: new Date().toISOString(),
  });

  // Store original profile data for canceling edits
  const [originalProfileData, setOriginalProfileData] = useState({});

  // Login history
  const [loginHistory, setLoginHistory] = useState([
    { date: new Date().toISOString(), device: 'Chrome on Windows', location: 'Current session' },
    { date: new Date(Date.now() - 86400000).toISOString(), device: 'Mobile App on Android', location: 'New York, US' },
    { date: new Date(Date.now() - 172800000).toISOString(), device: 'Firefox on MacOS', location: 'New York, US' },
  ]);

  // Check authentication and fetch profile data
  useEffect(() => {
    const token = ls.get("token");
    
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchUserData = async () => {
      try {
        // Use the user API
        const response = await user_api.get_user_profile();
        console.log('API Response:', response); // Debug log
        
        if (response && response.status === 200) {
          // Handle the user data structure from the API
          const userData = response.data && response.data.user ? response.data.user : response.data;
          
          const formattedData = {
            fullName: userData.name || '',
            email: userData.email || '',
            phone: userData.phone || '',
            address: userData.address || '',
            city: userData.city || '',
            zipCode: userData.zipCode || '',
            country: userData.country || '',
            isVerified: userData.emailVerified || false,
            lastLogin: userData.lastLogin || new Date().toISOString()
          };
          
          console.log('Formatted user data:', formattedData); // Debug log
          
          setProfileData(formattedData);
          setOriginalProfileData(formattedData);
          
          // Fetch profile image if available
          if (userData.profileImage) {
            setProfilePicture(userData.profileImage);
          }
          
          // Fetch notification preferences if available
          if (userData.notificationPreferences) {
            setNotificationPreferences(userData.notificationPreferences);
          }
          
          // Fetch login history if available
          if (userData.loginHistory && userData.loginHistory.length > 0) {
            setLoginHistory(userData.loginHistory);
          }
        } else {
          showAlert('Failed to load profile data', 'error');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        showAlert('Error loading profile. Please try again.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const showAlert = (message, severity = 'success') => {
    setAlert({
      open: true,
      message,
      severity
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Cancel edit mode when changing tabs
    if (editMode) {
      cancelEdit();
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleEditMode = () => {
    if (editMode) {
      // Save changes
      handleProfileUpdate();
    } else {
      // Enter edit mode
      setEditMode(true);
    }
  };

  const cancelEdit = () => {
    // Reset to original data
    setProfileData({
      ...originalProfileData,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setEditMode(false);
  };

  const handleProfileUpdate = async (event) => {
    if (event) {
      event.preventDefault();
    }
    
    // Validate form
    if (!profileData.fullName || !profileData.email || !profileData.phone) {
      showAlert('All required fields are marked with *', 'error');
      return;
    }

    if (!validate(profileData.email)) {
      showAlert('Please enter a valid email address', 'error');
      return;
    }
    
    setIsSaving(true);
    try {
      // Use the user API method - mapping fields to what the backend expects
      const response = await user_api.update_user_profile({
        name: profileData.fullName,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        city: profileData.city,
        zipCode: profileData.zipCode,
        country: profileData.country
      });

      if (response && response.status === 200) {
        showAlert('Profile updated successfully', 'success');
        
        // If the email was changed, inform the user about verification
        if (response.data && response.data.message && response.data.message.includes('verify')) {
          showAlert(response.data.message, 'info');
        }
        
        // Update local state with backend response if available
        if (response.data && response.data.user) {
          const updatedData = {
            ...profileData,
            fullName: response.data.user.name || profileData.fullName,
            email: response.data.user.email || profileData.email,
            phone: response.data.user.phone || profileData.phone,
            isVerified: response.data.user.emailVerified || profileData.isVerified
          };
          
          setProfileData(updatedData);
          setOriginalProfileData({
            ...updatedData,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        } else {
          setOriginalProfileData({
            ...profileData,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }
        
        setEditMode(false);
      } else {
        showAlert(response?.data?.msg || 'Failed to update profile', 'error');
      }
    } catch (error) {
      showAlert(error.message || 'Error updating profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    
    // Validate password fields
    if (!profileData.currentPassword || !profileData.newPassword || !profileData.confirmPassword) {
      showAlert('All password fields are required', 'error');
      return;
    }

    if (profileData.newPassword !== profileData.confirmPassword) {
      showAlert('New passwords do not match', 'error');
      return;
    }

    if (profileData.newPassword.length < 6) {
      showAlert('Password must be at least 6 characters', 'error');
      return;
    }

    setIsChangingPassword(true);
    try {
      // Use the user API method
      const response = await user_api.update_password({
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword
      });

      if (response && response.status === 200) {
        showAlert('Password updated successfully', 'success');
        setProfileData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } else {
        showAlert(response?.data?.msg || 'Failed to update password', 'error');
      }
    } catch (error) {
      showAlert(error.message || 'Error updating password', 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleTogglePassword = (field) => {
    if (field === 'current') {
      setShowCurrentPassword(!showCurrentPassword);
    } else {
      setShowNewPassword(!showNewPassword);
    }
  };
  
  const handleNotificationChange = (event) => {
    const { name, checked } = event.target;
    setNotificationPreferences(prev => ({
      ...prev,
      [name]: checked
    }));
    
    // In a real app, you'd save this to the backend
    showAlert('Notification preferences updated', 'success');
  };
  
  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // In a real app, you'd upload this to the server
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicture(e.target.result);
        showAlert('Profile picture updated', 'success');
      };
      reader.readAsDataURL(file);
    }
  };
  
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  const handleVerifyAccount = async () => {
    try {
      // Call API to send verification email
      const response = await user_api.resend_verification_email();
      
      if (response && response.data && response.data.success) {
        showAlert('Verification email sent! Please check your email inbox and click the verification link.', 'success');
      } else {
        showAlert(response?.data?.message || 'Failed to send verification email', 'error');
      }
    } catch (error) {
      showAlert(error.message || 'Error sending verification email', 'error');
    }
  };
  
  {/* Email Verification Information Dialog */}
<Dialog 
  open={verificationDialogOpen} 
  onClose={() => setVerificationDialogOpen(false)} 
  maxWidth="sm" 
  fullWidth
  PaperProps={{
    sx: {
      background: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(100, 116, 139, 0.2)',
    }
  }}
>
  <DialogTitle sx={{ color: '#00F5FF' }}>
    Email Verification
  </DialogTitle>
  <DialogContent>
    <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
      We've sent a verification link to your email address:
    </Typography>
    <Typography variant="body1" sx={{ color: '#00F5FF', mb: 3 }}>
      {profileData.email}
    </Typography>
    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
      Please check your inbox and click the verification link to verify your email address. The link will expire in 24 hours.
    </Typography>
    <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 2 }}>
      If you don't see the email, please check your spam folder.
    </Typography>
  </DialogContent>
  <DialogActions sx={{ p: 3 }}>
    <Button 
      onClick={() => setVerificationDialogOpen(false)} 
      sx={{ color: '#FFFFFF' }}
    >
      Close
    </Button>
    <Button 
      onClick={handleVerifyAccount} 
      variant="contained"
      sx={{
        backgroundColor: '#00F5FF',
        color: '#0f172a',
        '&:hover': {
          backgroundColor: '#00d4e6',
        }
      }}
    >
      Resend Verification Email
    </Button>
  </DialogActions>
</Dialog>
  
  const handleOpenDeleteDialog = () => {
    setConfirmDeleteOpen(true);
  };
  
  const handleDeleteAccount = () => {
    // In a real app, you'd call the API to delete the account
    showAlert('Account deletion requested. You will receive a confirmation email.', 'info');
    setConfirmDeleteOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Custom styling objects for components
  const rootStyle = {
    position: 'relative',
    minHeight: 'calc(100vh - 64px)', // Subtract header height
    background: 'linear-gradient(to bottom right, #0f172a, #1e293b)',
    color: '#ffffff',
    overflow: 'auto',
    paddingTop: '80px', // Add padding at top to avoid header overlap
    paddingBottom: '40px'
  };

  const beforeStyle = {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))',
    zIndex: 1
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.8)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(100, 116, 139, 0.2)',
    padding: '24px',
    boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.1)',
    color: '#ffffff',
    width: '100%',
    marginBottom: '24px'
  };

  const textFieldStyle = {
    marginBottom: '20px',
    '& .MuiInputBase-root': { color: '#FFFFFF' },
    '& .MuiInputLabel-root': { color: '#00F5FF' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B3B3B3' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00F5FF' }
  };

  const avatarStyle = {
    width: 120,
    height: 120,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    backgroundPosition: 'center',
    backgroundSize: 'cover'
  };

  const avatarIconButtonStyle = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: 'rgb(6, 182, 212)',
    '&:hover': {
      backgroundColor: 'rgb(8, 145, 178)'
    }
  };

  const buttonStyle = {
    marginTop: 2,
    backgroundColor: 'rgb(6, 182, 212)',
    '&:hover': {
      backgroundColor: 'rgb(8, 145, 178)'
    }
  };
  
  const tabsStyle = {
    '& .MuiTab-root': { 
      color: '#B3B3B3', 
      '&.Mui-selected': { color: '#00F5FF' },
      '&:hover': { color: '#FFFFFF' },
    },
    '& .MuiTabs-indicator': { backgroundColor: '#00F5FF' }
  };
  
  const actionButtonStyle = {
    borderColor: '#00F5FF',
    color: '#00F5FF',
    marginRight: 2,
    marginBottom: 2,
    '&:hover': {
      borderColor: '#FFFFFF',
      backgroundColor: 'rgba(0, 245, 255, 0.1)'
    }
  };
  
  const dangerButtonStyle = {
    borderColor: '#FF5252',
    color: '#FF5252',
    marginTop: 2,
    '&:hover': {
      borderColor: '#FF0000',
      backgroundColor: 'rgba(255, 82, 82, 0.1)'
    }
  };
  
  const sectionHeadingStyle = {
    color: '#00F5FF',
    marginBottom: 2,
    marginTop: 3,
    fontWeight: 500
  };
  
  const verifiedBadgeStyle = {
    backgroundColor: profileData.isVerified ? '#4CAF50' : '#FFC107',
    color: '#FFFFFF',
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    marginLeft: 8,
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 'bold'
  };
  
  const loginHistoryItemStyle = {
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: 8,
    paddingBottom: 8
  };

  const headerButtonStyle = {
    backgroundColor: editMode ? '#FF5252' : 'rgb(6, 182, 212)',
    color: '#FFFFFF',
    marginLeft: 2,
    '&:hover': {
      backgroundColor: editMode ? '#FF0000' : 'rgb(8, 145, 178)'
    }
  };

  const readOnlyTextStyle = {
    color: '#FFFFFF',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: '20px',
    display: 'flex'
  };

  const fieldLabelStyle = {
    color: '#00F5FF',
    fontWeight: 'normal',
    minWidth: '120px',
    display: 'inline-block'
  };

  const fieldValueStyle = {
    flex: 1
  };

  if (isLoading) {
    return (
      <div style={rootStyle}>
        <div style={beforeStyle}></div>
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress sx={{ color: 'rgb(6, 182, 212)' }} />
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div style={rootStyle}>
      <div style={beforeStyle}></div>
      <Container maxWidth="lg">
        <Snackbar 
          open={alert.open} 
          autoHideDuration={6000} 
          onClose={() => setAlert(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            severity={alert.severity} 
            onClose={() => setAlert(prev => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {alert.message}
          </Alert>
        </Snackbar>

        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Paper elevation={0} sx={{ 
            background: 'rgba(15, 23, 42, 0.6)', 
            backdropFilter: 'blur(10px)',
            padding: '12px 24px',
            marginTop: '25px',
            borderRadius: '8px',
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                sx={{ color: '#FFFFFF', marginRight: 2 }}
                onClick={() => navigate(-1)}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" sx={{ color: '#FFFFFF' }}>
                Profile Settings
                <span style={verifiedBadgeStyle}>
                  {profileData.isVerified ? (
                    <><Verified sx={{ fontSize: 16, marginRight: 0.5 }} /> Verified</>
                  ) : (
                    'Not Verified'
                  )}
                </span>
              </Typography>
            </Box>
            <Box>
              {tabValue === 0 && (
                <>
                  <Button
                    variant="contained"
                    startIcon={editMode ? <Cancel /> : <Edit />}
                    onClick={toggleEditMode}
                    sx={headerButtonStyle}
                  >
                    {editMode ? 'Cancel' : 'Edit Profile'}
                  </Button>
                  {editMode && (
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleProfileUpdate}
                      sx={{
                        backgroundColor: 'rgb(6, 182, 212)',
                        color: '#FFFFFF',
                        marginLeft: 2,
                        '&:hover': {
                          backgroundColor: 'rgb(8, 145, 178)'
                        }
                      }}
                    >
                      Save Changes
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={4}>
              <Card sx={cardStyle}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <div 
                      style={{
                        ...avatarStyle,
                        backgroundImage: profilePicture ? `url(${profilePicture})` : 'none'
                      }}
                    >
                      {!profilePicture && (
                        <Typography variant="h3" sx={{ color: '#00F5FF' }}>
                          {profileData.fullName ? profileData.fullName.charAt(0).toUpperCase() : 'U'}
                        </Typography>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleProfilePictureUpload}
                      style={{ display: 'none' }}
                      accept="image/*"
                    />
                    <IconButton
                      sx={avatarIconButtonStyle}
                      onClick={triggerFileInput}
                    >
                      <CameraAlt sx={{ color: 'white' }} />
                    </IconButton>
                  </Box>
                  <Typography variant="h5" sx={{ color: '#FFFFFF', mt: 1 }}>
                    {profileData.fullName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    Member since {new Date(profileData.lastLogin).getFullYear()}
                  </Typography>
                  
                  {!profileData.isVerified && (
                    <Button 
                      variant="outlined" 
                      startIcon={<Verified />}
                      sx={actionButtonStyle}
                      onClick={handleVerifyAccount}
                      fullWidth
                      size="small"
                      style={{ marginTop: 16 }}
                    >
                      Verify Account
                    </Button>
                  )}
                </CardContent>
                
                <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
                
                <List component="nav" dense>
                  <ListItem button selected={tabValue === 0} onClick={() => setTabValue(0)}>
                    <ListItemIcon>
                      <Person sx={{ color: tabValue === 0 ? '#00F5FF' : '#FFFFFF' }} />
                    </ListItemIcon>
                    <ListItemText primary="Personal Information" sx={{ color: tabValue === 0 ? '#00F5FF' : '#FFFFFF' }} />
                  </ListItem>
                  
                  <ListItem button selected={tabValue === 1} onClick={() => setTabValue(1)}>
                    <ListItemIcon>
                      <Security sx={{ color: tabValue === 1 ? '#00F5FF' : '#FFFFFF' }} />
                    </ListItemIcon>
                    <ListItemText primary="Security" sx={{ color: tabValue === 1 ? '#00F5FF' : '#FFFFFF' }} />
                  </ListItem>
                  
                  <ListItem button selected={tabValue === 2} onClick={() => setTabValue(2)}>
                    <ListItemIcon>
                      <Notifications sx={{ color: tabValue === 2 ? '#00F5FF' : '#FFFFFF' }} />
                    </ListItemIcon>
                    <ListItemText primary="Notifications" sx={{ color: tabValue === 2 ? '#00F5FF' : '#FFFFFF' }} />
                  </ListItem>
                  
                  <ListItem button selected={tabValue === 3} onClick={() => setTabValue(3)}>
                    <ListItemIcon>
                      <History sx={{ color: tabValue === 3 ? '#00F5FF' : '#FFFFFF' }} />
                    </ListItemIcon>
                    <ListItemText primary="Login Activity" sx={{ color: tabValue === 3 ? '#00F5FF' : '#FFFFFF' }} />
                  </ListItem>
                </List>
                
                <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
                
                <Button 
                  variant="outlined" 
                  startIcon={<Delete />}
                  sx={dangerButtonStyle}
                  onClick={handleOpenDeleteDialog}
                  fullWidth
                >
                  Delete Account
                </Button>
              </Card>
            </Grid>
            
            {/* Right Column - Content */}
            <Grid item xs={12} md={8}>
              <Card sx={cardStyle}>
                {tabValue === 0 && (
                  <>
                    <Typography variant="h6" sx={sectionHeadingStyle}>
                      Personal Information
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 3 }}>
                      {editMode ? 'Update your personal details and contact information' : 'Your personal details and contact information'}
                    </Typography>
                    
                    {editMode ? (
                      <form>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Full Name *"
                              name="fullName"
                              value={profileData.fullName}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Person sx={{ color: '#00F5FF' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Email *"
                              name="email"
                              type="email"
                              value={profileData.email}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Email sx={{ color: '#00F5FF' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Phone Number *"
                              name="phone"
                              value={profileData.phone}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Phone sx={{ color: '#00F5FF' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={sectionHeadingStyle}>
                              Address Information
                            </Typography>
                          </Grid>
                          
                          {/* <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Street Address"
                              name="address"
                              value={profileData.address}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LocationOn sx={{ color: '#00F5FF' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid> */}
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="City"
                              name="city"
                              value={profileData.city}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Region"
                              name="region"
                              value={profileData.region}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                            />
                          </Grid>
                          
                          {/* <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Country"
                              name="country"
                              value={profileData.country}
                              onChange={handleInputChange}
                              sx={textFieldStyle}
                            />
                          </Grid> */}
                        </Grid>
                      </form>
                    ) : (
                      // Read-only view
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: '#FFFFFF', mb: 2 }}>
                          Contact Information
                        </Typography>
                        
                        <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            <Person sx={{ fontSize: 18, verticalAlign: 'text-bottom', marginRight: 1 }} />
                            Full Name:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.fullName || 'Not provided'}
                          </Typography>
                        </Box>
                        
                        <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            <Email sx={{ fontSize: 18, verticalAlign: 'text-bottom', marginRight: 1 }} />
                            Email:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.email || 'Not provided'}
                          </Typography>
                        </Box>
                        
                        <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            <Phone sx={{ fontSize: 18, verticalAlign: 'text-bottom', marginRight: 1 }} />
                            Phone:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.phone || 'Not provided'}
                          </Typography>
                        </Box>
                        
                        <Typography variant="subtitle1" sx={{ color: '#FFFFFF', mb: 2, mt: 4 }}>
                          Address Information
                        </Typography>
                        
                        {/* <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            <LocationOn sx={{ fontSize: 18, verticalAlign: 'text-bottom', marginRight: 1 }} />
                            Address:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.address || 'Not provided'}
                          </Typography>
                        </Box> */}
                        
                        <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            City:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.city || 'Not provided'}
                          </Typography>
                        </Box>
                        
                        <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            Region:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.region || 'Not provided'}
                          </Typography>
                        </Box>
                        
                        {/* <Box sx={readOnlyTextStyle}>
                          <Typography component="span" sx={fieldLabelStyle}>
                            Country:
                          </Typography>
                          <Typography component="span" sx={fieldValueStyle}>
                            {profileData.country || 'Not provided'}
                          </Typography>
                        </Box> */}
                      </Box>
                    )}
                  </>
                )}

                {tabValue === 1 && (
                  <>
                    <Typography variant="h6" sx={sectionHeadingStyle}>
                      Security Settings
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 3 }}>
                      Manage your password and account security
                    </Typography>
                    
                    <form onSubmit={handlePasswordUpdate}>
                      <Typography variant="subtitle1" sx={{ color: '#FFFFFF', mb: 2 }}>
                        Change Password
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Current Password"
                            name="currentPassword"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={profileData.currentPassword}
                            onChange={handleInputChange}
                            sx={textFieldStyle}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: '#00F5FF' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton 
                                    onClick={() => handleTogglePassword('current')} 
                                    edge="end"
                                    sx={{ color: '#00F5FF' }}
                                  >
                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="New Password"
                            name="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={profileData.newPassword}
                            onChange={handleInputChange}
                            sx={textFieldStyle}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton 
                                    onClick={() => handleTogglePassword('new')} 
                                    edge="end"
                                    sx={{ color: '#00F5FF' }}
                                  >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Confirm New Password"
                            name="confirmPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={profileData.confirmPassword}
                            onChange={handleInputChange}
                            sx={textFieldStyle}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton 
                                    onClick={() => handleTogglePassword('new')} 
                                    edge="end"
                                    sx={{ color: '#00F5FF' }}
                                  >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <LoadingButton 
                          variant="contained" 
                          type="submit" 
                          loading={isChangingPassword}
                          startIcon={<Lock />}
                          sx={buttonStyle}
                        >
                          Update Password
                        </LoadingButton>
                      </Box>
                    </form>
                    
                    <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />
                    
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFF', mb: 2 }}>
                      Two-Factor Authentication
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <FormControlLabel
                        control={<Switch color="primary" />}
                        label="Enable Two-Factor Authentication"
                        sx={{ color: '#FFFFFF' }}
                      />
                      <Tooltip title="Adds an extra layer of security to your account">
                        <IconButton sx={{ color: '#00F5FF' }}>
                          <Help sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 3 }}>
                      Two-factor authentication adds an extra layer of security to your account by requiring a verification code in addition to your password.
                    </Typography>
                    
                    <Button 
                      variant="outlined"
                      sx={actionButtonStyle}
                      startIcon={<Security />}
                    >
                      Set Up Two-Factor Authentication
                    </Button>
                  </>
                )}

                {tabValue === 2 && (
                  <>
                    <Typography variant="h6" sx={sectionHeadingStyle}>
                      Notification Preferences
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 3 }}>
                      Manage how you receive notifications and updates
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={notificationPreferences.emailNotifications}
                              onChange={handleNotificationChange}
                              name="emailNotifications"
                              color="primary"
                            />
                          }
                          label="Email Notifications"
                          sx={{ color: '#FFFFFF', display: 'flex' }}
                        />
                        <Typography variant="body2" sx={{ color: '#B3B3B3', ml: 4, mt: -1, mb: 2 }}>
                          Receive notifications about account activity via email
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={notificationPreferences.pushNotifications}
                              onChange={handleNotificationChange}
                              name="pushNotifications"
                              color="primary"
                            />
                          }
                          label="Push Notifications"
                          sx={{ color: '#FFFFFF', display: 'flex' }}
                        />
                        <Typography variant="body2" sx={{ color: '#B3B3B3', ml: 4, mt: -1, mb: 2 }}>
                          Receive push notifications on your devices
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={notificationPreferences.securityAlerts}
                              onChange={handleNotificationChange}
                              name="securityAlerts"
                              color="primary"
                            />
                          }
                          label="Security Alerts"
                          sx={{ color: '#FFFFFF', display: 'flex' }}
                        />
                        <Typography variant="body2" sx={{ color: '#B3B3B3', ml: 4, mt: -1, mb: 2 }}>
                          Receive alerts about important security events
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={notificationPreferences.marketingEmails}
                              onChange={handleNotificationChange}
                              name="marketingEmails"
                              color="primary"
                            />
                          }
                          label="Marketing Emails"
                          sx={{ color: '#FFFFFF', display: 'flex' }}
                        />
                        <Typography variant="body2" sx={{ color: '#B3B3B3', ml: 4, mt: -1, mb: 2 }}>
                          Receive updates about new features and promotions
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        sx={buttonStyle}
                      >
                        Save Preferences
                      </Button>
                    </Box>
                  </>
                )}

                {tabValue === 3 && (
                  <>
                    <Typography variant="h6" sx={sectionHeadingStyle}>
                      Login Activity
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 3 }}>
                      Review your recent login activity
                    </Typography>
                    
                    <List>
                      {loginHistory.map((login, index) => (
                        <ListItem key={index} sx={loginHistoryItemStyle}>
                          <ListItemIcon>
                            <Badge 
                              color={index === 0 ? "success" : "default"} 
                              variant="dot"
                              sx={{ '& .MuiBadge-badge': { right: 2, top: 5 } }}
                            >
                              <History sx={{ color: '#00F5FF' }} />
                            </Badge>
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                                {login.device}
                                {index === 0 && (
                                  <Typography 
                                    component="span" 
                                    sx={{ 
                                      bgcolor: '#4CAF50', 
                                      color: '#FFFFFF',
                                      borderRadius: 1,
                                      px: 1,
                                      py: 0.2,
                                      ml: 1,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Current
                                  </Typography>
                                )}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                                {login.location} • {formatDate(login.date)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button 
                      variant="outlined"
                      sx={{ ...actionButtonStyle, mt: 2 }}
                      startIcon={<Security />}
                    >
                      Log Out All Other Devices
                    </Button>
                  </>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
      
      {/* Account Verification Dialog */}
      {/* Email Verification Information Dialog */}
      <Dialog 
        open={verificationDialogOpen} 
        onClose={() => setVerificationDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(100, 116, 139, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#00F5FF' }}>
          Email Verification
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
            We've sent a verification link to your email address:
          </Typography>
          <Typography variant="body1" sx={{ color: '#00F5FF', mb: 3 }}>
            {profileData.email}
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
            Please check your inbox and click the verification link to verify your email address. The link will expire in 24 hours.
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 2 }}>
            If you don't see the email, please check your spam folder.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setVerificationDialogOpen(false)} 
            sx={{ color: '#FFFFFF' }}
          >
            Close
          </Button>
          <Button 
            onClick={handleVerifyAccount} 
            variant="contained"
            sx={{
              backgroundColor: '#00F5FF',
              color: '#0f172a',
              '&:hover': {
                backgroundColor: '#00d4e6',
              }
            }}
          >
            Resend Verification Email
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Account Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#FF5252' }}>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action cannot be undone, and all your data will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} sx={{ color: '#FF5252' }}>
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfileSettings;